import { useEffect, useRef, useState } from "react";
import "./DropdownMenu.css";
import chevronIcon from "@/assets/chevron.svg";
import downArrowIcon from "@/assets/down_arrow.svg";

export interface DropdownMenuItem {
  label: string;
  onClick: () => void;
}

interface DropdownMenuTrigger {
  /** "user" shows name + subtitle + chevron; "dots" shows three-dot icon */
  triggerVariant: "user" | "dots" | "label";
  userName?: string;
  userSubtitle?: string;
  triggerLabel?: string;
}

const triggerVariantClassMap: Record<DropdownMenuTrigger["triggerVariant"], string> = {
  dots: "dropdownMenu__trigger--dots",
  label: "dropdownMenu__trigger--label",
  user: "dropdownMenu__trigger--user",
};

type DropdownMenuProps = DropdownMenuTrigger & {
  items: DropdownMenuItem[];
  /** Align the dropdown panel relative to the trigger: left (default) or right */
  align?: "left" | "right";
  /** Extra class on the root wrapper */
  className?: string;
  /** Extra class on the trigger button */
  triggerClassName?: string;
  /** Extra class on the dropdown menu panel */
  menuClassName?: string;
};

export function DropdownMenu({
  items,
  triggerVariant,
  userName,
  userSubtitle,
  triggerLabel,
  align = "left",
  className = "",
  triggerClassName = "",
  menuClassName = "",
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  return (
    <div
      ref={wrapperRef}
      className={`dropdownMenu ${className}`}
      aria-haspopup="true"
      aria-expanded={isOpen}
    >
      <button
        type="button"
        className={`dropdownMenu__trigger ${triggerVariantClassMap[triggerVariant]} ${triggerClassName}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={triggerVariant === "dots" ? "Open menu" : undefined}
      >
        {triggerVariant === "user" ? (
          <>
            <span className="dropdownMenu__userInfo">
              <span className="dropdownMenu__userName">{userName}</span>
              {userSubtitle && (
                <span className="dropdownMenu__userSubtitle">
                  {userSubtitle}
                </span>
              )}
            </span>
            <span
              className={`dropdownMenu__chevron ${isOpen ? "dropdownMenu__chevron--open" : ""}`}
              aria-hidden="true"
            >
              <img src={downArrowIcon} alt="" width="16" height="16" />
            </span>
          </>
        ) : triggerVariant === "label" ? (
          <>
            <span className="dropdownMenu__labelText">{triggerLabel}</span>
            <span
              className={`dropdownMenu__chevron ${isOpen ? "dropdownMenu__chevron--open" : ""}`}
              aria-hidden="true"
            >
              <img src={chevronIcon} alt="" width="16" height="16" />
            </span>
          </>
        ) : (
          <span className="dropdownMenu__dotsIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <circle cx="3" cy="8" r="1.25" />
              <circle cx="8" cy="8" r="1.25" />
              <circle cx="13" cy="8" r="1.25" />
            </svg>
          </span>
        )}
      </button>

      {isOpen && (
        <ul
          role="menu"
          className={`dropdownMenu__panel dropdownMenu__panel--${align} ${menuClassName}`}
        >
          {items.map((item) => (
            <li key={item.label} role="none" className="dropdownMenu__item">
              <button
                role="menuitem"
                type="button"
                className="dropdownMenu__itemBtn"
                onClick={() => {
                  setIsOpen(false);
                  item.onClick();
                }}
              >
                <span className="dropdownMenu__itemContent">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
